import React from 'react';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  Link,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';

const Login = ({
  onLoginSuccess,
  initialEmail = '',
  startOnEmailSignIn = false,
  startOnResetRequest = false,
}) => {
  const [error, setError] = React.useState('');
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [showEmailForm, setShowEmailForm] = React.useState(
    startOnResetRequest ? false : startOnEmailSignIn
  );
  const [isCreatingAccount, setIsCreatingAccount] = React.useState(false);
  const [showResetRequest, setShowResetRequest] = React.useState(startOnResetRequest);
  const [email, setEmail] = React.useState(initialEmail);
  const [password, setPassword] = React.useState('');
  const [resetRequesting, setResetRequesting] = React.useState(false);
  const [resetRequestSent, setResetRequestSent] = React.useState(false);
  const [resetRequestError, setResetRequestError] = React.useState('');

  const resetForm = () => {
    setError('');
    setEmail('');
    setPassword('');
  };

  const resetResetRequestState = () => {
    setResetRequesting(false);
    setResetRequestSent(false);
    setResetRequestError('');
  };

  const goToMethodPicker = () => {
    setShowEmailForm(false);
    setIsCreatingAccount(false);
    setShowResetRequest(false);
    resetResetRequestState();
    resetForm();
  };

  const goToEmailSignIn = () => {
    setShowEmailForm(true);
    setIsCreatingAccount(false);
    setShowResetRequest(false);
    resetResetRequestState();
    resetForm();
  };

  const goToCreateAccount = () => {
    setIsCreatingAccount(true);
    setShowEmailForm(false);
    setShowResetRequest(false);
    resetResetRequestState();
    resetForm();
  };

  const goToResetRequest = () => {
    setShowResetRequest(true);
    setShowEmailForm(false);
    setIsCreatingAccount(false);
    setError('');
    resetResetRequestState();
  };

  const goBackFromResetRequest = () => {
    setShowResetRequest(false);
    setShowEmailForm(true);
    setIsCreatingAccount(false);
    resetResetRequestState();
  };

  const handleGoogleLogin = async () => {
    setIsLoggingIn(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      if (onLoginSuccess) onLoginSuccess(result.user);
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Failed to login. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setIsLoggingIn(true);
    setError('');
    try {
      if (isCreatingAccount) {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        if (onLoginSuccess) onLoginSuccess(result.user);
        return;
      }

      const result = await signInWithEmailAndPassword(auth, email, password);
      if (onLoginSuccess) onLoginSuccess(result.user);
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.code === 'auth/user-not-found'
        ? 'No account found with this email.'
        : error.code === 'auth/wrong-password'
        ? 'Incorrect password.'
        : error.code === 'auth/invalid-credential'
        ? 'Invalid email or password.'
        : error.code === 'auth/email-already-in-use'
        ? 'An account already exists with this email.'
        : error.code === 'auth/weak-password'
        ? 'Password should be at least 6 characters.'
        : error.code === 'auth/invalid-email'
        ? 'Please enter a valid email address.'
        : error.message || 'Failed to login. Please check your credentials.';
      setError(errorMsg);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setResetRequestError('Please enter your email address.');
      return;
    }

    setResetRequesting(true);
    setResetRequestError('');
    try {
      await sendPasswordResetEmail(auth, trimmed);
      setResetRequestSent(true);
    } catch (err) {
      console.error('Password reset request error:', err);
      if (err && err.code === 'auth/network-request-failed') {
        setResetRequestError('Network error. Please check your connection and try again.');
      } else if (err && typeof err.code === 'string' && err.code.startsWith('auth/')) {
        // Treat all Firebase auth errors as "sent" to prevent account enumeration.
        setResetRequestSent(true);
      } else {
        setResetRequestError('Network error. Please check your connection and try again.');
      }
    } finally {
      setResetRequesting(false);
    }
  };

  // View A: Method picker (default)
  const isMethodPicker = !showEmailForm && !isCreatingAccount && !showResetRequest;
  // View B: Email sign-in
  const isEmailSignIn = showEmailForm && !isCreatingAccount && !showResetRequest;
  // View C: Create account
  const isCreateAccount = isCreatingAccount && !showResetRequest;
  // View D: Reset request
  const isResetRequest = showResetRequest;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        sx={{
          width: 420,
          maxWidth: '90vw',
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 4,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: '#333333',
            }}
          >
            Reseller Pro
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: '#666666',
              mb: 3,
              fontWeight: 500,
            }}
          >
            {isResetRequest
              ? 'Reset your password'
              : isEmailSignIn
              ? 'Sign in with Email'
              : isCreateAccount
              ? 'Create your account'
              : 'Simple tools for resellers'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* View A — Method picker */}
          {isMethodPicker && (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', color: '#555', mb: 1 }}
              >
                New to Reseller Pro?{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={goToCreateAccount}
                  sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
                >
                  Create an account
                </Link>{' '}
                or Login with your Google account to get started.
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={goToEmailSignIn}
                startIcon={<EmailIcon />}
                sx={{
                  height: 48,
                  fontSize: 16,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Sign in with Email
              </Button>

              <Button
                variant="outlined"
                color="primary"
                onClick={handleGoogleLogin}
                disabled={isLoggingIn}
                startIcon={
                  isLoggingIn ? (
                    <CircularProgress size={20} />
                  ) : (
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      style={{ width: 20, height: 20 }}
                    />
                  )
                }
                sx={{
                  height: 48,
                  fontSize: 16,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                {isLoggingIn ? 'Signing in...' : 'Sign in with Google'}
              </Button>
            </Stack>
          )}

          {/* View B — Email sign-in */}
          {isEmailSignIn && (
            <Box component="form" onSubmit={handleEmailPasswordLogin} sx={{ width: '100%' }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoggingIn}
                  required
                  variant="outlined"
                  autoFocus
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoggingIn}
                  required
                  variant="outlined"
                />
                <Box sx={{ textAlign: 'right' }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={goToResetRequest}
                    disabled={isLoggingIn}
                    sx={{ fontWeight: 600 }}
                  >
                    Forgot password?
                  </Link>
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoggingIn}
                  sx={{
                    height: 48,
                    fontSize: 16,
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  {isLoggingIn ? 'Signing in...' : 'Sign In'}
                </Button>
                <Typography variant="body2" sx={{ textAlign: 'center', color: '#555' }}>
                  New to Reseller Pro?{' '}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={goToCreateAccount}
                    disabled={isLoggingIn}
                    sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
                  >
                    Create an account
                  </Link>
                </Typography>
                <Button
                  variant="text"
                  onClick={goToMethodPicker}
                  disabled={isLoggingIn}
                  sx={{ textTransform: 'none' }}
                >
                  Back
                </Button>
              </Stack>
            </Box>
          )}

          {/* View C — Create account */}
          {isCreateAccount && (
            <Box component="form" onSubmit={handleEmailPasswordLogin} sx={{ width: '100%' }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoggingIn}
                  required
                  variant="outlined"
                  autoFocus
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoggingIn}
                  required
                  variant="outlined"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoggingIn}
                  sx={{
                    height: 48,
                    fontSize: 16,
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  {isLoggingIn ? 'Creating account...' : 'Create Account'}
                </Button>
                <Typography variant="body2" sx={{ textAlign: 'center', color: '#555' }}>
                  Already have an account?{' '}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={goToEmailSignIn}
                    disabled={isLoggingIn}
                    sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
                  >
                    Sign in
                  </Link>
                </Typography>
                <Button
                  variant="text"
                  onClick={goToMethodPicker}
                  disabled={isLoggingIn}
                  sx={{ textTransform: 'none' }}
                >
                  Back
                </Button>
              </Stack>
            </Box>
          )}

          {/* View D — Reset request */}
          {isResetRequest && (
            <Box sx={{ width: '100%' }}>
              {resetRequestSent ? (
                <Stack spacing={2}>
                  <Alert severity="success">
                    If an account exists for this email, a password reset link has been sent.
                    Check your inbox.
                  </Alert>
                  <Button
                    variant="text"
                    onClick={goBackFromResetRequest}
                    sx={{ textTransform: 'none' }}
                  >
                    Back to sign in
                  </Button>
                </Stack>
              ) : (
                <Box component="form" onSubmit={handleResetRequest}>
                  <Stack spacing={2}>
                    {resetRequestError && (
                      <Alert severity="error">{resetRequestError}</Alert>
                    )}
                    <Typography variant="body2" sx={{ color: '#555' }}>
                      Enter your email address and we'll send you a link to reset your
                      password.
                    </Typography>
                    <TextField
                      fullWidth
                      type="email"
                      label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={resetRequesting}
                      required
                      variant="outlined"
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={resetRequesting}
                      startIcon={
                        resetRequesting ? <CircularProgress size={20} /> : null
                      }
                      sx={{
                        height: 48,
                        fontSize: 16,
                        fontWeight: 700,
                        textTransform: 'none',
                      }}
                    >
                      {resetRequesting ? 'Sending...' : 'Send reset email'}
                    </Button>
                    <Button
                      variant="text"
                      onClick={goBackFromResetRequest}
                      disabled={resetRequesting}
                      sx={{ textTransform: 'none' }}
                    >
                      Back to sign in
                    </Button>
                  </Stack>
                </Box>
              )}
            </Box>
          )}

        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
